import { z } from "zod";

/** Shared validation. The SAME schemas are re-validated server-side in the edge
 *  function (docs/SECURITY.md sec 6 - never trust the client). */

export const contactSchema = z.object({
  name: z.string().min(2, "Please enter your name").max(120),
  email: z.string().email("Enter a valid email"),
  phone: z.string().max(40).optional().or(z.literal("")),
  message: z.string().min(10, "Tell us a bit more").max(4000),
  // Honeypot - must stay empty. Bots fill hidden fields.
  company_website: z.string().max(0).optional().or(z.literal("")),
});
export type ContactInput = z.infer<typeof contactSchema>;

export const applicationSchema = z
  .object({
    type: z.enum(["driver", "carrier", "office"]),
    // Position slug from the careers page (optional; for context on the lead).
    position: z.string().max(80).optional().or(z.literal("")),
    // Free-text "what kind of job are you interested in" for general
    // (no-specific-posting) applications.
    interest: z.string().max(200).optional().or(z.literal("")),
    name: z.string().min(2, "Please enter your name").max(120),
    email: z.string().email("Enter a valid email"),
    phone: z.string().min(7, "Enter a valid phone").max(40),
    // Driver-specific (required-ness enforced per type in superRefine below)
    cdlNumber: z.string().max(40).optional().or(z.literal("")),
    cdlState: z.string().max(2).optional().or(z.literal("")),
    // "" -> undefined so an empty input doesn't coerce to 0 and pass "required".
    experienceYears: z.preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : Number(v)),
      z.number({ invalid_type_error: "Enter a number" }).min(0).max(60).optional(),
    ),
    endorsementTanker: z.boolean().optional(),
    endorsementHazmat: z.boolean().optional(),
    endorsementTwic: z.boolean().optional(),
    offloadAirCompressor: z.boolean().optional(),
    offloadPump: z.boolean().optional(),
    // Carrier-specific
    authority: z.union([z.enum(["own", "lease"]), z.literal("")]).optional(),
    truck: z.string().max(120).optional().or(z.literal("")),
    mcNumber: z.string().max(20).optional().or(z.literal("")),
    dotNumber: z.string().max(20).optional().or(z.literal("")),
    source: z
      .union([
        z.enum(["google", "indeed", "facebook", "referral", "truck", "other"]),
        z.literal(""),
      ])
      .optional(),
    referralName: z.string().max(120).optional().or(z.literal("")),
    consentToContact: z.boolean().optional(),
    notes: z.string().max(2000).optional().or(z.literal("")),
    company_website: z.string().max(0).optional().or(z.literal("")), // honeypot
  })
  .superRefine((val, ctx) => {
    const require = (path: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });

    if (val.type === "driver") {
      if (!/^[A-Za-z]{2}$/.test(val.cdlState ?? "")) {
        require("cdlState", "2-letter state");
      }
      if (val.experienceYears === undefined) {
        require("experienceYears", "Required");
      }
    }

    if (val.type === "carrier") {
      if (val.authority !== "own" && val.authority !== "lease") {
        require("authority", "Select an option");
      }
      if (val.authority === "own" && !val.dotNumber?.trim()) {
        require("dotNumber", "DOT number is required with your own authority");
      }
      if (!val.truck?.trim()) {
        require("truck", "Tell us about your truck");
      }
      if (val.experienceYears === undefined) {
        require("experienceYears", "Required");
      }
    }

    // A general office/other application carries no posting and no CDL/MC
    // context, so the applicant must say what they're looking for.
    if (val.type === "office" && !val.position && !val.interest?.trim()) {
      require("interest", "Tell us what kind of job you're interested in");
    }

    if (!val.source) {
      require("source", "Select how you heard about us");
    }
    if (val.source === "referral" && !val.referralName?.trim()) {
      require("referralName", "Enter the referral name");
    }
    if (!val.consentToContact) {
      require("consentToContact", "Required");
    }
  });
export type ApplicationInput = z.infer<typeof applicationSchema>;

export const trackSchema = z.object({
  reference: z
    .string()
    // References appear as "#2966972" on rate cons / in the portal — accept the
    // copy-pasted "#". Internal spaces are part of real references (e.g.
    // "TULSTAR SO 014115"), so keep them (collapsed to single spaces).
    .transform((v) => v.trim().replace(/^#\s*/, "").replace(/\s+/g, " "))
    .pipe(
      z
        .string()
        .min(4, "Enter a tracking or PO number")
        .max(40)
        .regex(/^[A-Za-z0-9\- ]+$/, "Letters, numbers, spaces, and dashes only"),
    ),
});
export type TrackInput = z.infer<typeof trackSchema>;
